import React from 'react';
import {Space, Table, Typography} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {useTranslation} from 'react-i18next';
import {PodExemptReportType} from '../../graphql/podExempt/types';
import PodExemptStatusTag from './PodExemptStatusTag';

interface Props {
  data: PodExemptReportType[];
  loading: boolean;
  onView: (record: PodExemptReportType) => void;
  /** 人工下线（仅生效中行展示） */
  onOffline: (record: PodExemptReportType) => void;
  dictNameOf?: (code: string) => string;
}

/**
 * DSP「已通过的地址」列表（对齐原型列序）：
 * 地址ID / 生效地址÷围栏（双行）/ 维度 / 地址类型 / 豁免原因 / 提报人÷DSP（双行）/
 * 生效时间 / 30天豁免单量 / 状态 / 操作（详情）。
 * 行数据仍是上报单（单地址/围栏与上报单一一对应），地址ID 取明细 targetId（对下游的豁免地址 ID）。
 */
const PodExemptEffectiveTable: React.FC<Props> = ({data, loading, onView, onOffline, dictNameOf}) => {
  const {t} = useTranslation();
  const nameOf = dictNameOf ?? ((code: string) => code);

  const columns: ColumnsType<PodExemptReportType> = [
    {
      title: t('podExempt.effective.colAddressId'),
      dataIndex: 'targetId',
      width: 100,
      render: (v: string | null, record) => (
        // 地址/围栏两表各自自增会撞号，展示前缀区分（下游定位用 target_type+target_id 复合键，无歧义）
        <Typography.Link onClick={() => onView(record)}>
          {v != null ? `${record.scopeType === 'FENCE' ? 'FE' : 'AD'}-${v}` : '-'}
        </Typography.Link>
      ),
    },
    {
      title: t('podExempt.effective.colAddress'),
      dataIndex: 'displayAddress',
      width: 260,
      render: (_, record) => {
        const metaParts = [record.zipcode, record.warehouseName ?? record.warehouseCode].filter(Boolean);
        if (record.scopeType === 'FENCE') {
          metaParts.push(
            record.addressCount != null
              ? t('podExempt.field.addressCountUnit', {count: record.addressCount})
              : t('podExempt.field.addressCounting'),
          );
        }
        return (
          <div className="leading-normal">
            <div>{record.displayAddress ?? '-'}</div>
            <div className="text-xs text-gray-400 mt-0.5">{metaParts.join(' · ')}</div>
          </div>
        );
      },
    },
    {
      title: t('podExempt.effective.colScope'),
      dataIndex: 'scopeType',
      width: 90,
      render: (v: string) =>
        v === 'FENCE' ? t('podExempt.scope.fence') : t('podExempt.scope.single'),
    },
    {
      title: t('podExempt.field.addressType'),
      dataIndex: 'addressType',
      width: 130,
      render: (v: string | null) => (v ? nameOf(v) : '-'),
    },
    {
      title: t('podExempt.effective.colReason'),
      dataIndex: 'reasonCodes',
      width: 200,
      render: (codes: string[]) => (codes ?? []).map(nameOf).join(', ') || '-',
    },
    {
      title: t('podExempt.effective.colReporter'),
      key: 'reporter',
      width: 140,
      render: (_, record) => (
        <div className="leading-normal">
          <div>{record.creatorName ?? '-'}</div>
          <div className="text-xs text-gray-400 mt-0.5">{record.dspName ?? '-'}</div>
        </div>
      ),
    },
    {
      title: t('podExempt.effective.colEffectiveAt'),
      dataIndex: 'effectiveAt',
      width: 160,
      render: (v: string | null) => (v ? new Date(v).toLocaleString() : '-'),
    },
    {
      title: t('podExempt.effective.colHits'),
      dataIndex: 'hit30d',
      width: 120,
      render: (v: string | null) =>
        v != null ? t('podExempt.effective.hitsUnit', {count: Number(v)}) : '-',
    },
    {
      title: t('podExempt.field.status'),
      dataIndex: 'status',
      width: 100,
      render: (_, record) => <PodExemptStatusTag status={record.status} effectiveLibrary />,
    },
    {
      title: t('podExempt.common.actions'),
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Typography.Link onClick={() => onView(record)}>{t('podExempt.common.detail')}</Typography.Link>
          {record.status === 'EFFECTIVE' && (
            <Typography.Link type="danger" onClick={() => onOffline(record)}>
              {t('podExempt.offline.action')}
            </Typography.Link>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table<PodExemptReportType>
      rowKey="id"
      size="small"
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={false}
      scroll={{x: 'max-content'}}
    />
  );
};

export default PodExemptEffectiveTable;
